import http from './http/index';

export interface UserProfileData {
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  preferredName?: string;
  bio?: string;
  pronouns?: string;
  timezone?: string;
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
  lifeStage?: string;
  careerStage?: string;
  relationshipStatus?: string;
  relationships?: Array<{
    type: string; // "partner", "wife", "husband", "ex-partner", "parent", "child", "sibling", "friend", "other", etc.
    name: string;
    email?: string; // optional - may not be available if person isn't in system
    age?: number;
    pronouns?: string;
    userId?: string;
  }>;
  // Backward compatibility fields
  partner?: {
    name: string;
    email?: string; // optional - may not be available if person isn't in system
    age?: number;
    pronouns?: string;
    userId?: string;
  } | null;
  partnerName?: string; // Keep for backward compatibility
  partnerAge?: number; // Keep for backward compatibility
  partnerPronouns?: string; // Keep for backward compatibility
  children?: Array<{
    name: string;
    email?: string; // optional - may not be available if person isn't in system
    age: number;
    pronouns?: string;
    userId?: string;
  }>;
  hasChildren?: boolean; // Keep for backward compatibility
  childrenAges?: string[]; // Keep for backward compatibility
  industry?: string;
  role?: string;
  company?: string;
  teamSize?: string;
  managementLevel?: string;
  jobDuties?: string;
  interests?: string[];
  preferredTone?: string;
  communicationStyle?: string;
  motivationalStyle?: string;
  birthday?: string;
  zodiacSign?: string;
  mbtiType?: string;
  enneagramType?: string;
  dashboardLayout?: {
    version: number;
    rows?: string[][];
    widgets: Array<{
      id: string;
      order: number;
      visible: boolean;
      width: '33%' | '40%' | '50%' | '60%' | '67%' | 'full';
      recentsCount?: number;
      settings?: Record<string, unknown>;
    }>;
  };
  ariaOnboarding?: {
    status?: 'not_started' | 'account_created' | 'interests_selected' | 
           'assessment_intro_viewed' | 'assessment_started' | 
           'assessment_completed' | 'first_chat_complete';
    completedAt?: {
      accountCreated?: Date;
      interestsSelected?: Date;
      assessmentIntroViewed?: Date;
      assessmentStarted?: Date;
      assessmentCompleted?: Date;
      firstChatComplete?: Date;
    };
  };
}

export interface UserProfileResponse {
  success: boolean;
  data: UserProfileData;
  message?: string;
  error?: string;
}

export interface ProfileStatsResponse {
  success: boolean;
  data: {
    totalProfiles: number;
    profilesWithBio: number;
    profilesWithInterests: number;
    profilesWithLocation: number;
  };
}

class UserProfileAPI {
  private baseUrl = '/api/user-profile';

  /**
   * Get current user's profile
   */
  async getUserProfile(): Promise<UserProfileData> {
    try {
      const response = await http.get<UserProfileResponse>(this.baseUrl);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Create or update user profile
   */
  async saveUserProfile(profileData: UserProfileData): Promise<UserProfileData> {
    try {
      const response = await http.post<UserProfileResponse>(this.baseUrl, profileData);
      return response.data.data;
    } catch (error) {
      console.error('Error saving user profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile (PUT)
   */
  async updateUserProfile(profileData: Partial<UserProfileData>): Promise<UserProfileData> {
    try {
      const response = await http.put<UserProfileResponse>(this.baseUrl, profileData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Partial update of user profile (PATCH)
   */
  async patchUserProfile(profileData: Partial<UserProfileData>): Promise<UserProfileData> {
    try {
      const response = await http.patch<UserProfileResponse>(this.baseUrl, profileData);
      return response.data.data;
    } catch (error) {
      console.error('Error patching user profile:', error);
      throw error;
    }
  }

  /**
   * Delete user profile
   */
  async deleteUserProfile(): Promise<void> {
    try {
      await http.delete(this.baseUrl);
    } catch (error) {
      console.error('Error deleting user profile:', error);
      throw error;
    }
  }

  /**
   * Get profile statistics (admin only)
   */
  async getProfileStats(): Promise<ProfileStatsResponse['data']> {
    try {
      const response = await http.get<ProfileStatsResponse>(`${this.baseUrl}/stats`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching profile stats:', error);
      throw error;
    }
  }

  /**
   * Search profiles by interests
   */
  async searchProfilesByInterests(interests: string[]): Promise<UserProfileData[]> {
    try {
      const response = await http.get<{ success: boolean; data: UserProfileData[] }>(
        `${this.baseUrl}/search/interests`,
        { params: { interests } }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error searching profiles by interests:', error);
      throw error;
    }
  }

  /**
   * Search profiles by industry
   */
  async searchProfilesByIndustry(industry: string): Promise<UserProfileData[]> {
    try {
      const response = await http.get<{ success: boolean; data: UserProfileData[] }>(
        `${this.baseUrl}/search/industry`,
        { params: { industry } }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error searching profiles by industry:', error);
      throw error;
    }
  }

  /**
   * Search profiles by location
   */
  async searchProfilesByLocation(country?: string, state?: string): Promise<UserProfileData[]> {
    try {
      const params: any = {};
      if (country) params.country = country;
      if (state) params.state = state;

      const response = await http.get<{ success: boolean; data: UserProfileData[] }>(
        `${this.baseUrl}/search/location`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error searching profiles by location:', error);
      throw error;
    }
  }

  /**
   * Update user name information (first name, last name)
   */
  async updateUserName(firstName: string, lastName: string): Promise<any> {
    try {
      const user = JSON.parse(localStorage.getItem("user") as string);
      if (!user?.user?.userId) {
        throw new Error('No authenticated user found');
      }
      
      // Use API route instead of admin route so standard users can update their own name
      const response = await http.put(`/api/users/user-profile/${user.user.userId}`, {
        firstName,
        lastName,
        user: { userId: user.user.userId }
      });
      
      // Update localStorage with the new name so it stays in sync
      try {
        const localUserData = JSON.parse(localStorage.getItem("user") || "{}");
        if (localUserData?.user) {
          localUserData.user.firstName = firstName;
          localUserData.user.lastName = lastName;
          localStorage.setItem("user", JSON.stringify(localUserData));
        }
      } catch (localStorageError) {
        console.warn('Error updating localStorage after name change:', localStorageError);
        // Continue even if localStorage update fails
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating user name:', error);
      throw error;
    }
  }

  /**
   * Get current user's basic information
   */
  async getCurrentUserInfo(): Promise<{ firstName: string; lastName: string; email: string; userId: string }> {
    try {
      const user = JSON.parse(localStorage.getItem("user") as string);
      if (!user?.user) {
        throw new Error('No authenticated user found');
      }
      
      return {
        firstName: user.user.firstName || '',
        lastName: user.user.lastName || '',
        email: user.user.email || '',
        userId: user.user.userId || user.user._id || ''
      };
    } catch (error) {
      console.error('Error getting current user info:', error);
      throw error;
    }
  }

  /**
   * Get a specific user's profile (admin only)
   */
  async getUserProfileByUserId(userId: string): Promise<UserProfileData> {
    try {
      // Use admin route for getting other users' profiles
      // Server returns empty profile if none exists (for admins)
      const response = await http.get<UserProfileResponse>(`/admin/user-profile/${userId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user profile by ID:', error);
      throw error;
    }
  }

  /**
   * Update a specific user's profile (admin only)
   */
  async updateUserProfileByUserId(userId: string, profileData: Partial<UserProfileData>): Promise<UserProfileData> {
    try {
      const response = await http.put<UserProfileResponse>(`/admin/user-profile/${userId}`, profileData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating user profile by ID:', error);
      throw error;
    }
  }


  /**
   * Strip fields that should not be round-tripped on section saves.
   * dashboardLayout is persisted via PATCH from the dashboard; including it here
   * can fail validation when unrelated sections (e.g. bio) are saved.
   */
  prepareProfilePayloadForSave(profile: Partial<UserProfileData>): Partial<UserProfileData> {
    const payload = { ...profile } as Partial<UserProfileData> & Record<string, unknown>;
    delete payload.firstName;
    delete payload.lastName;
    delete payload.email;
    delete payload.userId;
    delete payload.dashboardLayout;
    delete payload._id;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.__v;
    // Search-added relationships start with no type until the user picks one. The schema
    // requires type and name, so sending an unfinished row fails the whole save.
    if (Array.isArray(payload.relationships)) {
      payload.relationships = payload.relationships.filter(
        (r) => r && r.type?.trim() && r.name?.trim()
      );
    }
    return payload;
  }

  /** Surface server validation messages from axios error responses. */
  getProfileSaveErrorMessage(error: unknown, fallback: string): string {
    const err = error as {
      response?: { data?: { message?: string; errors?: string[] } };
      message?: string;
    };
    const serverErrors = err.response?.data?.errors;
    if (Array.isArray(serverErrors) && serverErrors.length > 0) {
      return serverErrors.join(', ');
    }
    return err.response?.data?.message || err.message || fallback;
  }

  /**
   * Validate profile data before sending to server
   */
  validateProfileData(data: Partial<UserProfileData>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate interests array
    if (data.interests && !Array.isArray(data.interests)) {
      errors.push('Interests must be an array');
    }



    // Validate children ages array
    if (data.childrenAges && !Array.isArray(data.childrenAges)) {
      errors.push('Children ages must be an array');
    }

    // Validate birthday if provided
    if (data.birthday && isNaN(Date.parse(data.birthday))) {
      errors.push('Invalid birthday format');
    }

    // Validate timezone if provided
    if (data.timezone && !this.isValidTimezone(data.timezone)) {
      errors.push('Invalid timezone');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private isValidTimezone(timezone: string): boolean {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return true;
    } catch (error) {
      return false;
    }
  }
}

const userProfileAPI = new UserProfileAPI();
export default userProfileAPI;